import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Header } from '@/components/common/Header';
import { useIngestionPipeline } from '@/hooks/useIngestionPipeline';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { IngestedFileRecord, PhotoRecord } from '@/types/domain';
import { PolarstepsLocationPoint, PolarstepsStep } from '@/types/polarsteps';
import { Compass } from 'lucide-react';
import { TravelMap } from '@/components/map/TravelMap';
import { CategoryPills } from '@/components/map/CategoryPills';
import { TimelineSlider } from '@/components/map/TimelineSlider';
import { LightboxViewer } from '@/components/viewer/LightboxViewer';
import {
  TravelCatalogView,
  CatalogTripItem,
  ICELAND_COVER_SVG,
  JAPAN_COMING_SOON_COVER_SVG,
} from '@/components/catalog/TravelCatalogView';
import { AdminIngestView } from '@/components/admin/AdminIngestView';
import { OnboardingView } from '@/components/onboarding/OnboardingView';
import {
  photosToGeoJson,
  splitRouteAtAntiMeridian,
  computeCategoryCounts,
  computeTripTimeBounds,
} from '@/services/mapGeoJson';
import { generateSampleTrip } from '@/services/sampleTrip';
import { storageService, sortPhotosChronologically } from '@/services/storage';
import { useRemoteViewer } from '@/hooks/useRemoteViewer';
import { ReadOnlyShareView } from '@/components/viewer/ReadOnlyShareView';
import { ExportDialog } from '@/components/share/ExportDialog';

interface BundledTripData {
  tripName: string;
  timezoneId: string;
  steps: PolarstepsStep[];
  locations: PolarstepsLocationPoint[];
  photos: PhotoRecord[];
  categories: string[];
  startDate?: number;
  endDate?: number;
  coverKey?: string;
}

let cachedBundledTrip: BundledTripData | null | undefined = undefined;

export async function fetchBundledTrip(): Promise<BundledTripData | null> {
  if (cachedBundledTrip !== undefined) return cachedBundledTrip;
  try {
    const res = await fetch('/data/manifest.json');
    if (!res.ok) {
      cachedBundledTrip = null;
      return null;
    }
    const manifest = await res.json();
    const [tripRes, photosRes] = await Promise.all([
      fetch('/data/trip.json'),
      fetch('/data/photos.json'),
    ]);
    if (!tripRes.ok || !photosRes.ok) {
      cachedBundledTrip = null;
      return null;
    }
    const trip = await tripRes.json();
    const rawPhotos = await photosRes.json();

    const normalizedPhotos: PhotoRecord[] = rawPhotos.map((p: any) => ({
      ...p,
      thumbnailKey: `/photos/thumb/${p.id}.webp`,
      previewKey: `/photos/hd/${p.id}.webp`,
      thumbnailUrl: `/photos/thumb/${p.id}.webp`,
      previewUrl: `/photos/hd/${p.id}.webp`,
    }));

    const sorted = sortPhotosChronologically(normalizedPhotos);
    const categories =
      Array.isArray(manifest.stats?.categories) && manifest.stats.categories.length > 0
        ? manifest.stats.categories
        : ['Chiaretti'];

    cachedBundledTrip = {
      tripName: trip.name || 'La nostra Islanda',
      timezoneId: trip.timezone_id || 'Atlantic/Reykjavik',
      steps: trip.all_steps || [],
      locations: trip.locations || [],
      photos: sorted,
      categories,
      startDate: trip.start_date || sorted[0]?.timestampUtc,
      endDate: trip.end_date || sorted[sorted.length - 1]?.timestampUtc,
      coverKey: sorted[0]?.previewKey || sorted[0]?.thumbnailKey,
    };
    return cachedBundledTrip;
  } catch {
    cachedBundledTrip = null;
    return null;
  }
}

export const App: React.FC = () => {
  const remoteViewer = useRemoteViewer();
  const pwa = usePwaInstall();
  const { canInstall, promptInstall } = pwa;

  const [stagedPhotos, setStagedPhotos] = useState<IngestedFileRecord[]>([]);
  const [stagedPolarsteps, setStagedPolarsteps] = useState<{
    locationsFile?: File;
    tripFile?: File;
  }>({});

  // View state: 'onboarding' (initial greeting) | 'catalog' (default public portfolio) | 'map' | 'admin' (ingestion)
  const [appView, setAppView] = useState<'onboarding' | 'catalog' | 'map' | 'admin'>(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash.toLowerCase();
      const params = new URLSearchParams(window.location.search);
      if (hash === '#admin' || params.get('mode') === 'admin') {
        return 'admin';
      }
      if (hash === '#map') {
        return 'map';
      }
      if (hash === '#onboarding' || params.get('view') === 'onboarding') {
        return 'onboarding';
      }
      const completed = sessionStorage.getItem('chiaretti_onboarding_completed');
      if (completed !== 'true') {
        return 'onboarding';
      }
    }
    return 'catalog';
  });

  const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false);

  // Map state
  const [tripName, setTripName] = useState<string>('Madeira Explorer 2024');
  const [timezoneId, setTimezoneId] = useState<string>('Atlantic/Madeira');
  const [photos, setPhotos] = useState<PhotoRecord[]>([]);
  const [locations, setLocations] = useState<PolarstepsLocationPoint[]>([]);
  const [steps, setSteps] = useState<PolarstepsStep[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('Tutte');
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);

  // Catalog items list
  const [catalogTrips, setCatalogTrips] = useState<CatalogTripItem[]>([]);

  // Lightbox Level-2 state
  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  const [lightboxPhotoId, setLightboxPhotoId] = useState<string | null>(null);

  const pipeline = useIngestionPipeline({
    throttleMs: 100,
  });

  // Reconcile and refresh catalog trips from IndexedDB + Personal Trips
  const refreshCatalogTrips = useCallback(async () => {
    const tripsList: CatalogTripItem[] = [];

    try {
      const storedPhotos = await storageService.getPhotos();
      const storedTrips = await storageService.getAllTrips();

      if (storedTrips.length > 0) {
        for (const trip of storedTrips) {
          const tripPhotos = storedPhotos.filter((p) => {
            if (!trip.start_date || !trip.end_date) return true;
            return p.timestampUtc >= trip.start_date && p.timestampUtc <= trip.end_date;
          });
          const photosForTrip = tripPhotos.length > 0 ? tripPhotos : storedPhotos;
          const sorted = sortPhotosChronologically(photosForTrip);
          const uniqueCategories = Array.from(
            new Set(sorted.map((p) => p.category).filter(Boolean))
          );

          let tripDisplayName = trip.name;
          if (
            !tripDisplayName ||
            tripDisplayName.toLowerCase().includes('polarsteps') ||
            tripDisplayName.toLowerCase().includes('madeira') ||
            tripDisplayName === 'Traccia Polarsteps' ||
            tripDisplayName === 'Viaggio Polarsteps'
          ) {
            tripDisplayName = 'La nostra Islanda';
          }

          tripsList.push({
            id: trip.id,
            name: tripDisplayName,
            startDate: trip.start_date || sorted[0]?.timestampUtc,
            endDate: trip.end_date || sorted[sorted.length - 1]?.timestampUtc,
            photoCount: sorted.length,
            stepCount: trip.all_steps?.length || 0,
            categories: uniqueCategories.length > 0 ? uniqueCategories : ['Panorami', 'Con me', 'Dettagli', 'On the Road'],
            coverKey: sorted[0]?.previewKey || sorted[0]?.thumbnailKey,
            coverDataUrl: sorted.length === 0 ? ICELAND_COVER_SVG : undefined,
            isDemo: false,
          });
        }
      } else if (storedPhotos.length > 0) {
        const sorted = sortPhotosChronologically(storedPhotos);
        const uniqueCategories = Array.from(
          new Set(sorted.map((p) => p.category).filter(Boolean))
        );

        tripsList.push({
          id: 'stored-active-trip',
          name: 'La nostra Islanda',
          startDate: sorted[0]?.timestampUtc,
          endDate: sorted[sorted.length - 1]?.timestampUtc,
          photoCount: sorted.length,
          stepCount: 0,
          categories: uniqueCategories.length > 0 ? uniqueCategories : ['Panorami', 'Con me', 'Dettagli', 'On the Road'],
          coverKey: sorted[0]?.previewKey || sorted[0]?.thumbnailKey,
          isDemo: false,
        });
      } else {
        const bundled = await fetchBundledTrip();
        if (bundled) {
          tripsList.push({
            id: 'islanda-trip',
            name: bundled.tripName,
            startDate: bundled.startDate,
            endDate: bundled.endDate,
            photoCount: bundled.photos.length,
            stepCount: bundled.steps.length,
            categories: bundled.categories,
            coverKey: bundled.coverKey,
            isDemo: false,
          });
        } else {
          // Fallback card for Iceland
          tripsList.push({
            id: 'islanda-trip',
            name: 'La nostra Islanda',
            startDate: 1718000000,
            endDate: 1718000000 + 7 * 86400,
            photoCount: 143,
            stepCount: 0,
            categories: ['Panorami', 'Con me', 'Dettagli', 'On the Road'],
            coverDataUrl: ICELAND_COVER_SVG,
            isDemo: false,
          });
        }
      }
    } catch (err) {
      console.warn('Errore lettura catalog da IndexedDB:', err);
      const bundled = await fetchBundledTrip();
      if (bundled) {
        tripsList.push({
          id: 'islanda-trip',
          name: bundled.tripName,
          startDate: bundled.startDate,
          endDate: bundled.endDate,
          photoCount: bundled.photos.length,
          stepCount: bundled.steps.length,
          categories: bundled.categories,
          coverKey: bundled.coverKey,
          isDemo: false,
        });
      } else {
        tripsList.push({
          id: 'islanda-trip',
          name: 'La nostra Islanda',
          startDate: 1718000000,
          endDate: 1718000000 + 7 * 86400,
          photoCount: 143,
          stepCount: 0,
          categories: ['Panorami', 'Con me', 'Dettagli', 'On the Road'],
          coverDataUrl: ICELAND_COVER_SVG,
          isDemo: false,
        });
      }
    }

    // Second Card: Giappone with Coming Soon tag
    tripsList.push({
      id: 'coming-soon-giappone',
      name: 'Giappone',
      photoCount: 0,
      stepCount: 0,
      categories: [],
      coverDataUrl: JAPAN_COMING_SOON_COVER_SVG,
      isComingSoon: true,
      isDemo: false,
    });

    setCatalogTrips(tripsList);
  }, []);

  // Synchronize URL hash for #admin, #map, and #onboarding routing
  useEffect(() => {
    const handleUrlChange = () => {
      const hash = window.location.hash.toLowerCase();
      const params = new URLSearchParams(window.location.search);
      if (hash === '#admin' || params.get('mode') === 'admin') {
        setAppView('admin');
      } else if (hash === '#map') {
        setAppView('map');
      } else if (hash === '#onboarding' || params.get('view') === 'onboarding') {
        setAppView('onboarding');
      } else {
        const completed = sessionStorage.getItem('chiaretti_onboarding_completed');
        if (completed !== 'true' && hash === '') {
          setAppView('onboarding');
        } else {
          setAppView('catalog');
        }
      }
    };

    window.addEventListener('hashchange', handleUrlChange);
    window.addEventListener('popstate', handleUrlChange);

    return () => {
      window.removeEventListener('hashchange', handleUrlChange);
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []);

  const handleCompleteOnboarding = useCallback(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('chiaretti_onboarding_completed', 'true');
      if (window.location.hash === '#onboarding') {
        window.location.hash = '';
      }
    }
    setAppView('catalog');
  }, []);

  // On mount, hydrate stored trip and prepare catalog
  useEffect(() => {
    async function hydrateStoredTrip() {
      try {
        const storedPhotos = await storageService.getPhotos();
        if (storedPhotos.length > 0) {
          const sorted = sortPhotosChronologically(storedPhotos);
          setPhotos(sorted);
          const storedTrips = await storageService.getAllTrips();
          const activeTrip = storedTrips[0];
          if (activeTrip) {
            let name = activeTrip.name;
            if (
              !name ||
              name.toLowerCase().includes('polarsteps') ||
              name.toLowerCase().includes('madeira') ||
              name === 'Traccia Polarsteps' ||
              name === 'Viaggio Polarsteps'
            ) {
              name = 'La nostra Islanda';
            }
            setTripName(name);
            setTimezoneId(activeTrip.timezone_id || 'Atlantic/Reykjavik');
            setSteps(activeTrip.all_steps || []);
            setLocations(activeTrip.locations || []);
          } else {
            setTripName('La nostra Islanda');
          }
        } else {
          const bundled = await fetchBundledTrip();
          if (bundled) {
            setPhotos(bundled.photos);
            setTripName(bundled.tripName);
            setTimezoneId(bundled.timezoneId);
            setSteps(bundled.steps);
            setLocations(bundled.locations);
          }
        }
      } catch (err) {
        console.warn('Hydration from local DB bypassed or failed:', err);
      }
      refreshCatalogTrips();
    }
    hydrateStoredTrip();
  }, [refreshCatalogTrips]);

  // Refresh catalog whenever ingestion finishes
  useEffect(() => {
    if (pipeline.status === 'completed') {
      refreshCatalogTrips();
    }
  }, [pipeline.status, refreshCatalogTrips]);

  // Load sample trip by default or on user request
  const loadDemoTrip = useCallback(() => {
    const demo = generateSampleTrip();
    setTripName('La nostra Islanda');
    setTimezoneId(demo.trip.timezone_id || 'Atlantic/Reykjavik');
    setPhotos(demo.photos);
    setLocations(demo.locations);
    setSteps(demo.steps);
    const bounds = computeTripTimeBounds(demo.locations, demo.photos);
    setTimeRange(bounds);
    setSelectedCategory('Tutte');
    setSelectedPhotoId(null);
    setAppView('map');
    if (typeof window !== 'undefined') {
      window.location.hash = 'map';
    }
  }, []);

  // Handler to open map after real ingestion or selection
  const handleOpenIngestedMap = useCallback(async (selectedTripId?: string | number) => {
    try {
      const storedPhotos = await storageService.getPhotos();
      const storedTrips = await storageService.getAllTrips();

      if (storedPhotos.length > 0) {
        const sorted = sortPhotosChronologically(storedPhotos);
        setPhotos(sorted);
        const activeTrip =
          (selectedTripId ? storedTrips.find((t) => t.id === selectedTripId) : null) ||
          storedTrips.find((t) => t.id === 'islanda-trip' || t.name === 'La nostra Islanda') ||
          storedTrips[0];
        const activeLocations = activeTrip?.locations || [];
        let name = activeTrip?.name;
        if (
          !name ||
          name.toLowerCase().includes('polarsteps') ||
          name.toLowerCase().includes('madeira') ||
          name === 'Traccia Polarsteps' ||
          name === 'Viaggio Polarsteps'
        ) {
          name = 'La nostra Islanda';
        }
        setTripName(name);
        setTimezoneId(activeTrip?.timezone_id || 'Atlantic/Reykjavik');
        setSteps(activeTrip?.all_steps || []);
        setLocations(activeLocations);
        const bounds = computeTripTimeBounds(activeLocations, sorted);
        setTimeRange(bounds);
        setSelectedCategory('Tutte');
        setSelectedPhotoId(null);
        setAppView('map');
        if (typeof window !== 'undefined') {
          window.location.hash = 'map';
        }
      } else {
        const bundled = await fetchBundledTrip();
        if (bundled) {
          setPhotos(bundled.photos);
          setTripName(bundled.tripName);
          setTimezoneId(bundled.timezoneId);
          setSteps(bundled.steps);
          setLocations(bundled.locations);
          const bounds = computeTripTimeBounds(bundled.locations, bundled.photos);
          setTimeRange(bounds);
          setSelectedCategory('Tutte');
          setSelectedPhotoId(null);
          setAppView('map');
          if (typeof window !== 'undefined') {
            window.location.hash = 'map';
          }
        } else {
          loadDemoTrip();
        }
      }
    } catch (err) {
      console.error('Errore durante il caricamento del viaggio elaborato:', err);
      loadDemoTrip();
    }
  }, [loadDemoTrip]);

  // Handler invoked when a trip trace or photo set is updated from admin
  const handleTripUpdated = useCallback(async () => {
    try {
      const storedTrips = await storageService.getAllTrips();
      const activeTrip =
        storedTrips.find((t) => t.id === 'islanda-trip' || t.name === 'La nostra Islanda') ||
        storedTrips[0];
      if (activeTrip) {
        setLocations(activeTrip.locations || []);
        setSteps(activeTrip.all_steps || []);
        setTimezoneId(activeTrip.timezone_id || 'Atlantic/Reykjavik');
        setTripName(activeTrip.name || 'La nostra Islanda');
      }
      const storedPhotos = await storageService.getPhotos();
      if (storedPhotos.length > 0) {
        setPhotos(sortPhotosChronologically(storedPhotos));
      }
    } catch (e) {
      console.warn('Errore refresh stato viaggio:', e);
    }
    refreshCatalogTrips();
  }, [refreshCatalogTrips]);

  // Handler for trip selection from the TravelCatalogView
  const handleSelectTrip = useCallback(
    (tripId: string | number, _isDemo?: boolean) => {
      if (tripId === 'coming-soon-giappone') {
        return;
      }
      handleOpenIngestedMap(tripId);
    },
    [handleOpenIngestedMap]
  );

  // Navigation handlers
  const navigateToAdmin = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.hash = 'admin';
    }
    setAppView('admin');
  }, []);

  const navigateToCatalog = useCallback(() => {
    if (typeof window !== 'undefined' && (window.location.hash === '#admin' || window.location.hash === '#map')) {
      window.location.hash = '';
      if (window.history?.pushState) {
        window.history.pushState(null, '', window.location.pathname + window.location.search);
      }
    }
    setAppView('catalog');
  }, []);

  // Handler to completely reset trip storage (only accessible in admin mode)
  const handleResetTrip = useCallback(async () => {
    try {
      await storageService.clearAll();
      setPhotos([]);
      setLocations([]);
      setSteps([]);
      setTripName('Viaggio Polarsteps');
      setTimezoneId('UTC');
      setSelectedPhotoId(null);
      setSelectedCategory('Tutte');
      pipeline.reset();
      setStagedPhotos([]);
      setStagedPolarsteps({});
      await refreshCatalogTrips();
    } catch (err) {
      console.error('Errore durante il reset del viaggio:', err);
    }
  }, [pipeline, refreshCatalogTrips]);

  // Overall time bounds calculation
  const [minTripTime, maxTripTime] = useMemo(() => {
    return computeTripTimeBounds(locations, photos);
  }, [locations, photos]);

  const [timeRange, setTimeRange] = useState<[number, number]>([minTripTime, maxTripTime]);

  // Synchronize timeRange when trip bounds change
  useEffect(() => {
    setTimeRange([minTripTime, maxTripTime]);
  }, [minTripTime, maxTripTime]);

  // Category statistics & counts
  const categoryStats = useMemo(() => {
    return computeCategoryCounts(photos);
  }, [photos]);

  // Filtered GeoJSON Point FeatureCollection for Supercluster
  const photosGeoJson = useMemo(() => {
    return photosToGeoJson(photos, {
      filter: {
        category: selectedCategory,
        timeRange,
      },
    });
  }, [photos, selectedCategory, timeRange]);

  // Filtered Route MultiLineString for trajectory overlay with anti-meridian protection
  const routeGeoJson = useMemo(() => {
    const feature = splitRouteAtAntiMeridian(locations, {
      timeRange,
    });
    return {
      type: 'FeatureCollection' as const,
      features: [feature],
    };
  }, [locations, timeRange]);

  // Filtered array of photos matching active category and timeRange
  const filteredPhotos = useMemo(() => {
    const list = photos.filter((p) => {
      if (selectedCategory !== 'Tutte' && p.category !== selectedCategory) return false;
      if (p.timestampUtc < timeRange[0] || p.timestampUtc > timeRange[1]) return false;
      return true;
    });
    return sortPhotosChronologically(list);
  }, [photos, selectedCategory, timeRange]);

  const handlePhotoSelect = useCallback((id: string) => {
    setSelectedPhotoId(id);
  }, []);

  const handlePhotoExpand = useCallback((id: string) => {
    setLightboxPhotoId(id);
    setLightboxOpen(true);
  }, []);

  // When URL points to a remote shared trip (#trip=... or ?trip=...), mount ReadOnlyShareView
  if (remoteViewer.isShareMode) {
    return (
      <ReadOnlyShareView
        hydratedData={remoteViewer.hydratedData}
        isLoading={remoteViewer.isLoading}
        error={remoteViewer.error}
        onRetry={remoteViewer.retry}
        onExitReadOnly={remoteViewer.exitReadOnly}
        manifestUrl={remoteViewer.manifestUrl}
      />
    );
  }

  // When in onboarding view, render full-screen birthday onboarding greeting
  if (appView === 'onboarding') {
    return <OnboardingView onComplete={handleCompleteOnboarding} />;
  }

  return (
    <div className="flex flex-col h-full bg-slate-900 text-slate-100 overflow-hidden font-sans">
      <Header
        tripName={appView === 'map' ? tripName : undefined}
        showDashboardButton={appView === 'map'}
        onBackToDashboard={navigateToCatalog}
        onOpenExport={appView === 'map' && photos.length > 0 ? () => setExportDialogOpen(true) : undefined}
        onResetTrip={appView === 'admin' && photos.length > 0 ? handleResetTrip : undefined}
        onOpenAdmin={navigateToAdmin}
        isAdmin={appView === 'admin'}
        viewMode={appView}
        pwaInstall={pwa}
      />

      <main className="flex-1 relative overflow-hidden flex flex-col">
        {/* VIEW 1: Interactive Map Exploration */}
        {appView === 'map' && (
          <div className="relative w-full h-full overflow-hidden select-none">
            {/* MapLibre GL JS GPU WebGL Canvas */}
            <TravelMap
              photosGeoJson={photosGeoJson}
              routeGeoJson={routeGeoJson}
              selectedPhotoId={selectedPhotoId}
              onPhotoSelect={handlePhotoSelect}
              onPhotoExpand={handlePhotoExpand}
              className="w-full h-full"
            />

            {/* Top Glassmorphic Navigation & Filter Bar */}
            <div className="absolute top-3 left-3 right-3 z-30 flex items-center justify-between gap-2 pointer-events-none">
              {/* Dynamic Category Pill Filters */}
              {categoryStats.categories.length > 0 && (
                <div className="pointer-events-auto overflow-x-auto no-scrollbar">
                  <CategoryPills
                    categories={categoryStats.categories}
                    counts={categoryStats.counts}
                    selectedCategory={selectedCategory}
                    onSelectCategory={setSelectedCategory}
                  />
                </div>
              )}

              {/* Floating Trip Info Badge */}
              <div className="pointer-events-auto hidden sm:flex items-center gap-2 px-3 py-1.5 bg-slate-900/90 backdrop-blur-md border border-slate-700/80 rounded-xl shadow-xl text-xs text-slate-200 shrink-0">
                <Compass className="w-3.5 h-3.5 text-sky-400" />
                <span className="font-semibold text-white truncate max-w-[140px] md:max-w-none">
                  {tripName}
                </span>
                <span className="text-slate-500">•</span>
                <span className="text-slate-400 font-mono text-[11px]">
                  {photosGeoJson.features.length} / {photos.length} foto
                </span>
              </div>
            </div>

            {/* Bottom Timeline Slider Drawer */}
            <div className="absolute bottom-4 left-3 right-3 z-30 pointer-events-none max-w-2xl mx-auto">
              <TimelineSlider
                minTime={minTripTime}
                maxTime={maxTripTime}
                currentTimeRange={timeRange}
                onChange={setTimeRange}
                steps={steps}
                onStepSelect={(step) => {
                  console.log('Selected step:', step.name);
                }}
                timezoneId={timezoneId}
              />
            </div>

            {/* Level-2 Fullscreen Lightbox */}
            {lightboxOpen && (
              <LightboxViewer
                photos={filteredPhotos.length > 0 ? filteredPhotos : photos}
                initialPhotoId={lightboxPhotoId ?? undefined}
                isOpen={lightboxOpen}
                onClose={() => {
                  setLightboxOpen(false);
                  setLightboxPhotoId(null);
                }}
                onPhotoChange={(photo) => {
                  setSelectedPhotoId(photo.id);
                }}
                timezoneId={timezoneId}
                onLocateOnMap={(photo) => {
                  setSelectedPhotoId(photo.id);
                }}
              />
            )}
          </div>
        )}

        {/* VIEW 2: Public Premium Travel Catalog (Default) */}
        {appView === 'catalog' && (
          <TravelCatalogView
            trips={catalogTrips}
            onSelectTrip={handleSelectTrip}
            onOpenAdmin={navigateToAdmin}
            canInstall={canInstall}
            onInstall={promptInstall}
          />
        )}

        {/* VIEW 3: Admin Ingestion & Data Management View (#admin) */}
        {appView === 'admin' && (
          <AdminIngestView
            pipeline={pipeline}
            stagedPhotos={stagedPhotos}
            setStagedPhotos={setStagedPhotos}
            stagedPolarsteps={stagedPolarsteps}
            setStagedPolarsteps={setStagedPolarsteps}
            onOpenIngestedMap={handleOpenIngestedMap}
            onResetTrip={handleResetTrip}
            onBackToCatalog={navigateToCatalog}
            hasStoredData={photos.length > 0}
            storedPhotoCount={photos.length}
            onTripUpdated={handleTripUpdated}
          />
        )}
      </main>

      {/* Standalone ZIP Export Dialog */}
      <ExportDialog
        isOpen={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      />
    </div>
  );
};

export default App;
